import React, { useRef, useState, useCallback } from "react";
import { withJsonFormsControlProps } from "@jsonforms/react";
import SFButton from "../../button";
import { rankWith, schemaMatches } from "@jsonforms/core";
import { SFImageIcon, SFUploadIcon } from "../../icons";
import SFTextField from "../../textField";
import SFAvatar from "../../avatar";
import getStyled from "../../../hooks/getStyled";
import { SFStack } from "../../grid";
import { useTranslation } from "react-i18next";
import SFTypography from "../../typography";
import { useDispatch } from "react-redux";
import { toastActions } from "@base-provider";

const { styled } = getStyled();

const SFAvatarStyled = styled(SFAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.grey50.main,
  border: `1px ${theme.palette.grey250.main} solid`,
  borderRadius: "4px",
  color: theme.palette.grey450.main,
  marginRight: "8px",
}));

const StyledSFStack = styled(SFStack)(({ theme }) => ({
  width: "100%",
}));

const ButtonStack = styled(SFStack)(({ theme }) => ({
  marginLeft: theme.spacing(3),
}));

const SvgUploadButtonRenderer = ({
  data,
  handleChange,
  path,
  uischema,
  required,
  enabled,
  errors,
  onChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { i18nKey, label, options } = uischema;
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [fileContent, setFileContent] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const validateFile = (file) => {
    if (file.type !== "image/svg+xml") {
      return t("components.formBuilder.errors.invalidFileType");
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB max size
      return t("components.formBuilder.errors.fileTooLarge");
    }
    return null;
  };

  const validateSvgDimensions = (content) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(content, "image/svg+xml");
    const svgElement = svgDoc.querySelector("svg");
  
    if (svgElement) {
      const width = parseFloat(svgElement.getAttribute("width"));
      const height = parseFloat(svgElement.getAttribute("height"));
      if (width > 200 || height > 200) {
        return t("components.formBuilder.errors.invalidSvgDimensions");
      }
    } else {
      return t("components.formBuilder.errors.invalidFileType");
    }
    return null;
  };
  

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const fileError = validateFile(file);
      if (fileError) {
        setErrorMessage(fileError);
        resetFileInput();
        return;
      }

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const content = e.target.result;
        const svgError = validateSvgDimensions(content);
        if (svgError) {
          setErrorMessage(svgError);
          resetFileInput();
          return;
        }
        dispatch(toastActions.showSuccessMessage(t("components.formBuilder.messages.fileUploadSuccess")));
        setFileName(file.name);
        setFileContent(content);
        handleChange(path, { fileName: file.name, fileContent: content });
        setErrorMessage(""); // Clear any previous error message
      };

      fileReader.readAsText(file);
    }
  }, [handleChange, path]);

  const resetFileInput = () => {
    setFileName("");
    setFileContent("");
    handleChange(path, null); // Clear the form data
  };

  return (
    <SFStack direction="column">
      <SFStack direction="row" alignItems="center">
        <SFAvatarStyled variant="square" icon={<SFImageIcon />} />
        <StyledSFStack>
          <SFTextField
            label={i18nKey ? t(i18nKey) : label}
            value={data?.fileName || fileName || "EYLogo.svg"}
            variant="filled"
            readOnly
            required
            onChange={onChange}
            errors={errorMessage ? [errorMessage] : errors}
            maxLength={options?.maxLength}
            disabled
          />
          {options?.helperText && (
            <SFTypography variant="tableList" align="left">
              {t(options?.helperText)}
            </SFTypography>
          )}
        </StyledSFStack>
        <ButtonStack>
          <SFButton disabled={options.readOnly} variant={options.variant} onClick={handleButtonClick}>
            <SFUploadIcon />
            {t("components.formBuilder.buttonLabels.upload")}
          </SFButton>
        </ButtonStack>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <input type="hidden" name={`${path}.fileContent`} value={fileContent} />
      </SFStack>
   
    </SFStack>
  );
};

export const SvgUploadButtonTester = rankWith(
  106,
  schemaMatches((schema) => schema.upload)
);

export default withJsonFormsControlProps(SvgUploadButtonRenderer);
